import React, { useEffect, useState } from 'react';
import { History, ArrowUpRight, ArrowDownRight, RefreshCcw } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { QuotaTransaction } from '../types/index.js';

export const QuotaHistory: React.FC = () => {
  const [history, setHistory] = useState<QuotaTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.getQuotaHistory();
        setHistory(res.items || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const getTypeBadge = (type: QuotaTransaction['type']) => {
    switch (type) {
      case 'consume':
        return <EduBadge variant="success" dot>Consumo</EduBadge>;
      case 'reservation':
        return <EduBadge variant="warning" dot>Reserva</EduBadge>;
      case 'release':
        return <EduBadge variant="info" dot>Liberação</EduBadge>;
      case 'bonus':
        return <EduBadge variant="orange" dot>Bônus</EduBadge>;
      default:
        return <EduBadge variant="neutral" dot>Ajuste</EduBadge>;
    }
  };

  return (
    <div>
      <Header
        title="Histórico de Consumo"
        subtitle="Extrato detalhado e auditável de todas as movimentações da sua franquia de aulas"
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <EduCard
          title="Extrato de Transações de Franquia"
          subtitle="Cada reserva, consumo definitivo e liberação de videoaula é registrado com chave de idempotência"
        >
          {loading ? (
            <div className="p-12 text-center text-xs text-txt-muted">Carregando extrato...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-10 text-xs text-txt-secondary">
              Nenhuma movimentação registrada no ciclo atual.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-bdr text-txt-secondary font-semibold bg-surface-secondary">
                    <th className="py-3 px-4">Data e Hora</th>
                    <th className="py-3 px-4">Tipo</th>
                    <th className="py-3 px-4">Movimentação</th>
                    <th className="py-3 px-4">Saldo Após</th>
                    <th className="py-3 px-4">Motivo / Descrição</th>
                    <th className="py-3 px-4">Referência</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-bdr text-txt-primary">
                  {history.map((tx) => (
                    <tr key={tx.id} className="hover:bg-surface-secondary/40">
                      <td className="py-3 px-4 font-mono text-txt-secondary">
                        {new Date(tx.createdAt).toLocaleString('pt-BR')}
                      </td>
                      <td className="py-3 px-4">{getTypeBadge(tx.type)}</td>
                      <td className="py-3 px-4 font-bold">
                        {tx.type === 'bonus' || tx.type === 'release' ? (
                          <span className="text-status-success flex items-center gap-0.5">
                            <ArrowUpRight size={14} /> +{tx.amount}
                          </span>
                        ) : (
                          <span className="text-status-error flex items-center gap-0.5">
                            <ArrowDownRight size={14} /> -{tx.amount}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-bold font-mono">
                        {tx.balanceAfter} aulas
                      </td>
                      <td className="py-3 px-4 max-w-xs truncate text-txt-secondary">
                        {tx.reason || '—'}
                      </td>
                      <td className="py-3 px-4 font-mono text-[11px] text-txt-muted">
                        {tx.referenceJobId ? `Job #${tx.referenceJobId.substring(0, 8)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </EduCard>
      </div>
    </div>
  );
};
